// ── Undo tree ─────────────────────────────────────────────────────────────────
// Branching edit history, after Vim's undo tree as mbbill/undotree shows it:
// every state an editor reaches is a node, numbered in the order it was made.
// Undo moves to the parent; an edit made after an undo starts a new branch
// beside the undone one instead of discarding it, so no state is ever lost and
// any node can be jumped back to. Redo follows the branch last made or visited
// from the current node.
//
// Pure and generic over the state it holds: values in, new tree out. The
// caller owns applying a node's state to its editor.

export interface UndoNode<S> {
  /** Order the node was made in; the root is 0. */
  seq: number;
  /** Null for the root. */
  parent: number | null;
  state: S;
  /** When the node was made or last merged into, as epoch milliseconds. */
  at: number;
  /** What the edit that made it did, for the history. */
  label: string;
  /** Consecutive edits of one group merge into one node (see `commit`). */
  group?: string;
}

export interface UndoTree<S> {
  /** By `seq`. */
  nodes: readonly UndoNode<S>[];
  current: number;
  /** By node: the child redo moves to. */
  redoTo: Readonly<Record<number, number>>;
}

/** Edits of one group this close together are one step of the history. */
export const UNDO_MERGE_WINDOW_MS = 1500;

export function createUndoTree<S>(
  state: S,
  { at, label }: { at: number; label: string },
): UndoTree<S> {
  return {
    nodes: [{ seq: 0, parent: null, state, at, label }],
    current: 0,
    redoTo: {},
  };
}

function nodeAt<S>(tree: UndoTree<S>, seq: number): UndoNode<S> {
  const node = tree.nodes[seq];
  if (!node) throw new Error(`Undo tree has no node ${seq}`);
  return node;
}

export function currentNode<S>(tree: UndoTree<S>): UndoNode<S> {
  return nodeAt(tree, tree.current);
}

export function childrenOf<S>(tree: UndoTree<S>, seq: number): UndoNode<S>[] {
  return tree.nodes.filter((node) => node.parent === seq);
}

/**
 * Takes back the current node, the newest one: the merge that made it
 * returned to its parent's state, so it no longer changes anything.
 */
function dropCurrent<S>(tree: UndoTree<S>, parent: number): UndoTree<S> {
  const redoTo = { ...tree.redoTo };
  delete redoTo[parent];
  const sibling = childrenOf(tree, parent)
    .filter((node) => node.seq !== tree.current)
    .at(-1);
  if (sibling) redoTo[parent] = sibling.seq;
  return {
    nodes: tree.nodes.slice(0, -1),
    current: parent,
    redoTo,
  };
}

/** One edit to record: when it was made, and what it did. */
export interface UndoStep<S> {
  at: number;
  /** What the edit did, from the state just before it. */
  label: string;
  group?: string;
  /**
   * How long after the group's last edit this one still merges into it;
   * default {@link UNDO_MERGE_WINDOW_MS}. A group whose edits arrive apart but
   * belong together (one assistant turn) passes a longer window.
   */
  mergeWindowMs?: number;
  /**
   * What the edit did from `base`, the state the step it merges into started
   * from: a rename typed key by key is named from the name before the first
   * key, not the one before the last. Without it the merged step keeps `label`.
   */
  relabel?: (base: S) => string;
}

/**
 * Records `state` as the next step after the current node. An edit in the same
 * `group` as the current node, within its merge window of it and
 * with nothing made after it, replaces that node's state instead (typing a
 * title is one step, not one per keystroke); one that returns to the state
 * before that node takes the node back (typing a character, then deleting it,
 * is no step at all). The root never merges.
 */
export function commit<S>(
  tree: UndoTree<S>,
  state: S,
  { at, label, group, relabel, mergeWindowMs }: UndoStep<S>,
  equals: (a: S, b: S) => boolean = Object.is,
): UndoTree<S> {
  const current = currentNode(tree);
  const merges =
    group !== undefined &&
    current.parent !== null &&
    current.group === group &&
    at - current.at <= (mergeWindowMs ?? UNDO_MERGE_WINDOW_MS) &&
    childrenOf(tree, current.seq).length === 0;
  if (
    merges &&
    current.parent !== null &&
    current.seq === tree.nodes.length - 1 &&
    equals(nodeAt(tree, current.parent).state, state)
  )
    return dropCurrent(tree, current.parent);
  if (merges) {
    // The merged step runs from its parent's state, not the last keystroke's.
    const base = current.parent === null ? null : nodeAt(tree, current.parent);
    const merged = (base && relabel?.(base.state)) ?? label;
    return {
      ...tree,
      nodes: tree.nodes.map((node) =>
        node.seq === current.seq ? { ...node, state, at, label: merged } : node,
      ),
    };
  }
  const seq = tree.nodes.length;
  return {
    nodes: [
      ...tree.nodes,
      { seq, parent: current.seq, state, at, label, group },
    ],
    current: seq,
    redoTo: { ...tree.redoTo, [current.seq]: seq },
  };
}

export function canUndo<S>(tree: UndoTree<S>): boolean {
  return currentNode(tree).parent !== null;
}

export function canRedo<S>(tree: UndoTree<S>): boolean {
  return tree.redoTo[tree.current] !== undefined;
}

/** Moves to the parent, remembering the branch to redo along. */
export function undo<S>(tree: UndoTree<S>): UndoTree<S> {
  const { parent } = currentNode(tree);
  if (parent === null) return tree;
  return {
    ...tree,
    current: parent,
    redoTo: { ...tree.redoTo, [parent]: tree.current },
  };
}

export function redo<S>(tree: UndoTree<S>): UndoTree<S> {
  const next = tree.redoTo[tree.current];
  return next === undefined ? tree : { ...tree, current: next };
}

/** The nodes from the root to `seq`, root first. */
export function pathTo<S>(tree: UndoTree<S>, seq: number): UndoNode<S>[] {
  const path: UndoNode<S>[] = [];
  for (let node: UndoNode<S> | undefined = nodeAt(tree, seq); node;) {
    path.unshift(node);
    node = node.parent === null ? undefined : nodeAt(tree, node.parent);
  }
  return path;
}

/**
 * The tree cut to at most `maxNodes` nodes, for storage that can't hold it
 * all: it keeps the current node, what redo would bring back from it, and as
 * many of its newest ancestors as fit; other branches and the oldest steps go.
 * The oldest node kept becomes the root, so undo stops there.
 */
export function trimUndoTree<S>(
  tree: UndoTree<S>,
  maxNodes: number,
): UndoTree<S> {
  if (tree.nodes.length <= maxNodes) return tree;
  const limit = Math.max(1, maxNodes);
  const redoChain: number[] = [];
  for (
    let next = tree.redoTo[tree.current];
    next !== undefined && redoChain.length < limit - 1;
    next = tree.redoTo[next]
  )
    redoChain.push(next);
  const line = pathTo(tree, tree.current)
    .slice(-(limit - redoChain.length))
    .map((node) => node.seq);
  const kept = [...line, ...redoChain];
  const renumbered = new Map(kept.map((seq, index) => [seq, index]));
  const nodes = kept.map((seq, index) => {
    const node = nodeAt(tree, seq);
    return {
      ...node,
      seq: index,
      parent:
        index === 0 || node.parent === null
          ? null
          : (renumbered.get(node.parent) ?? null),
    };
  });
  const redoTo: Record<number, number> = {};
  for (const [from, to] of Object.entries(tree.redoTo)) {
    const keptFrom = renumbered.get(Number(from));
    const keptTo = renumbered.get(to);
    if (keptFrom !== undefined && keptTo !== undefined)
      redoTo[keptFrom] = keptTo;
  }
  return { nodes, current: renumbered.get(tree.current) ?? 0, redoTo };
}

/**
 * Makes `seq` the current node. Redo from each of its ancestors then leads
 * back to it, so undoing away from a jumped-to state and redoing returns there.
 */
export function jumpTo<S>(tree: UndoTree<S>, seq: number): UndoTree<S> {
  const path = pathTo(tree, seq);
  const redoTo = { ...tree.redoTo };
  for (let i = 0; i + 1 < path.length; i++) {
    const node = path[i];
    const child = path[i + 1];
    if (node && child) redoTo[node.seq] = child.seq;
  }
  return { ...tree, current: seq, redoTo };
}

/** A line from a node up to one of its children, as the graph draws it. */
export interface UndoGraphEdge {
  /** The lane the line comes from: its child's lane, from the row above. */
  fromLane: number;
  /** On the path from the root to the current node. */
  isActive: boolean;
}

export interface HistoryGraphRow<N> {
  node: N;
  /** The node's lane, 0 leftmost. */
  lane: number;
  /** Lines from the top of the row into the node, one per child drawn above it. */
  edges: UndoGraphEdge[];
  /** Whether a line leaves the node downward, to its parent. */
  hasParent: boolean;
  /** Lanes a line passes straight through, waiting for a parent further down. */
  through: UndoGraphEdge[];
  isCurrent: boolean;
  isActive: boolean;
}

export interface HistoryGraphLayout<N> {
  rows: HistoryGraphRow<N>[];
  /** Lanes the widest row needs. */
  laneCount: number;
}

/**
 * The tree drawn as a graph the way undotree and `git log --graph` draw it:
 * newest node on top, each node below every child and joined to them by
 * lines. A node keeps the lane of its first child drawn; a branch that has
 * nowhere else to go opens a new lane to the right and folds back into its
 * parent's lane where the parent is drawn.
 */
export type UndoGraphRow<S> = HistoryGraphRow<UndoNode<S>>;
export type UndoGraph<S> = HistoryGraphLayout<UndoNode<S>>;

export interface LineageNode {
  id: string;
  parentId: string | null;
  order: number;
}

function lineageChildren<N extends LineageNode>(
  nodes: readonly N[],
): Map<string, number> {
  const remaining = new Map(nodes.map((node) => [node.id, 0]));
  for (const node of nodes) {
    if (node.parentId !== null && remaining.has(node.parentId))
      remaining.set(node.parentId, (remaining.get(node.parentId) ?? 0) + 1);
  }
  return remaining;
}

/** Newest available child first, with every parent below all its children. */
function lineageOrder<N extends LineageNode>(nodes: readonly N[]): N[] {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const remaining = lineageChildren(nodes);
  const ready = nodes.filter((node) => remaining.get(node.id) === 0);
  const ordered: N[] = [];
  while (ready.length) {
    ready.sort((a, b) => b.order - a.order || a.id.localeCompare(b.id));
    const node = ready.shift();
    if (!node) break;
    ordered.push(node);
    const parent = node.parentId === null ? undefined : byId.get(node.parentId);
    if (!parent) continue;
    const left = (remaining.get(parent.id) ?? 0) - 1;
    remaining.set(parent.id, left);
    if (left === 0) ready.push(parent);
  }
  if (ordered.length !== nodes.length)
    throw new Error('History graph must be a parent-linked forest');
  return ordered;
}

function lineagePath<N extends LineageNode>(
  byId: Map<string, N>,
  id: string | null,
): Set<string> {
  const active = new Set<string>();
  for (
    let node = id === null ? undefined : byId.get(id);
    node && !active.has(node.id);
    node = node.parentId === null ? undefined : byId.get(node.parentId)
  )
    active.add(node.id);
  return active;
}

/** A parent-linked forest; absent or deleted parents start a new root. */
export function lineageGraph<N extends LineageNode>(
  nodes: readonly N[],
  {
    currentId = null,
    highlightPathTo = currentId,
  }: {
    currentId?: string | null;
    highlightPathTo?: string | null;
  } = {},
): HistoryGraphLayout<N> {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const active = lineagePath(byId, highlightPathTo);
  const lanes: ({ parent: string; isActive: boolean } | null)[] = [];
  const rows: HistoryGraphRow<N>[] = [];
  let laneCount = 0;
  for (const node of lineageOrder(nodes)) {
    const incoming = lanes.flatMap((line, index) =>
      line?.parent === node.id
        ? [{ fromLane: index, isActive: line.isActive }]
        : [],
    );
    let lane = incoming[0]?.fromLane ?? lanes.indexOf(null);
    if (lane === -1) lane = lanes.length;
    const through = lanes.flatMap((line, index) =>
      line && line.parent !== node.id
        ? [{ fromLane: index, isActive: line.isActive }]
        : [],
    );
    for (const edge of incoming) lanes[edge.fromLane] = null;
    const isActive = active.has(node.id);
    const hasParent = node.parentId !== null && byId.has(node.parentId);
    lanes[lane] =
      hasParent && node.parentId !== null
        ? { parent: node.parentId, isActive }
        : null;
    while (lanes.length && lanes.at(-1) === null) lanes.pop();
    laneCount = Math.max(laneCount, lane + 1, lanes.length);
    rows.push({
      node,
      lane,
      edges: incoming,
      hasParent,
      through,
      isCurrent: node.id === currentId,
      isActive,
    });
  }
  return { rows, laneCount };
}

export function historyGraph<S>(tree: UndoTree<S>): UndoGraph<S> {
  const graph = lineageGraph(
    tree.nodes.map((node) => ({
      id: String(node.seq),
      parentId: node.parent === null ? null : String(node.parent),
      order: node.seq,
      undoNode: node,
    })),
    { currentId: String(tree.current) },
  );
  return {
    ...graph,
    rows: graph.rows.map((row) => ({ ...row, node: row.node.undoNode })),
  };
}

/** Only the edits leading to the state that will be saved or published. */
export function activePathTrail<S>(
  tree: UndoTree<S>,
): { label: string; at: number }[] {
  return pathTo(tree, tree.current)
    .slice(1)
    .map(({ label, at }) => ({ label, at }));
}
